import React, { useEffect, useState } from "react";
import { getProProfile, getAuthHeader } from "../api";
import { awsConfig } from "../aws-config";
import axios from "axios";

interface Template {
  id: string;
  name: string;
  type: "message" | "quote";
  service: string;
  body: string;
}

interface Props {
  lead: any;
  onClose: () => void;
}

export default function TemplatePicker({ lead, onClose }: Props) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [profile, setProfile] = useState<any>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filledBody, setFilledBody] = useState("");
  const [sending, setSending] = useState(false);
  const [step, setStep] = useState<"select" | "edit">("select");

  useEffect(() => {
    getProProfile().then(p => {
      setProfile(p);
      const tpls = p.messageTemplates || [];
      setTemplates(tpls);
    }).catch(() => {});
  }, []);

  function fillTemplate(body: string): string {
    const date = lead.scheduledDate ? new Date(lead.scheduledDate).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "TBD";
    return body
      .replace(/\{client_name\}/g, "there")
      .replace(/\{service_type\}/g, lead.serviceType || lead.category || "Service")
      .replace(/\{job_date\}/g, date)
      .replace(/\{pickup_address\}/g, lead.address || "")
      .replace(/\{destination_address\}/g, lead.destination || lead.address || "")
      .replace(/\{job_price\}/g, lead.estimatedCost ? "$" + lead.estimatedCost : "TBD")
      .replace(/\{company_name\}/g, profile?.businessName || profile?.name || "")
      .replace(/\{your_name\}/g, profile?.name || "")
      .replace(/\{phone\}/g, profile?.phone || "");
  }

  function selectTemplate(tpl: Template) {
    setSelectedId(tpl.id);
    setFilledBody(fillTemplate(tpl.body));
    setStep("edit");
  }

  async function sendMessage() {
    if (!filledBody.trim()) return;
    setSending(true);
    try {
      const headers = await getAuthHeader();
      const conversationId = `${lead.clientId}_${profile.proId || lead.proId}`;
      // Send via REST API endpoint that creates message
      await axios.post(`${awsConfig.apiUrl}/messages`, {
        conversationId,
        recipientId: lead.clientId,
        message: filledBody,
        senderId: profile.proId
      }, { headers });
      alert("Message sent!");
      onClose();
    } catch (e: any) {
      console.error(e);
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(filledBody);
      alert("Copied to clipboard! Paste it in chat to send.");
      onClose();
    } finally { setSending(false); }
  }

  function copyToClipboard() {
    navigator.clipboard.writeText(filledBody);
    alert("Copied to clipboard!");
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-bold text-navy text-lg">Send Template</h2>
            <p className="text-xs text-gray-400">{lead.serviceType || "New Lead"}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 text-2xl w-8 h-8 flex items-center justify-center">✕</button>
        </div>

        {/* Step 1: Select Template */}
        {step === "select" && (
          <div className="flex-1 overflow-y-auto p-4">
            {templates.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-3xl mb-2">📝</div>
                <div className="text-sm text-gray-400 mb-3">No templates yet</div>
                <a href="/pro/templates" className="text-mouna-blue font-bold text-sm">Create Templates →</a>
              </div>
            ) : (
              <div className="space-y-2">
                {templates.map(tpl => (
                  <button key={tpl.id} onClick={() => selectTemplate(tpl)}
                    className="w-full bg-gray-50 hover:bg-blue-pale rounded-2xl p-3 text-left transition-colors">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold rounded-full px-2 py-0.5 bg-white text-mouna-blue">
                        {tpl.type === "quote" ? "💰 Quote" : "💬 Message"}
                      </span>
                      <div className="font-bold text-navy text-sm">{tpl.name}</div>
                    </div>
                    <div className="text-xs text-gray-500 line-clamp-2">{tpl.body.slice(0, 120)}...</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Edit & Send */}
        {step === "edit" && (
          <>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="bg-blue-pale rounded-xl px-3 py-2 mb-3 text-xs text-mouna-blue font-bold">
                ✨ Auto-filled with lead details — edit before sending
              </div>
              <textarea value={filledBody} onChange={e => setFilledBody(e.target.value)}
                rows={14}
                className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm outline-none focus:border-mouna-blue resize-y leading-relaxed"/>
            </div>
            <div className="px-4 py-3 border-t border-gray-100 flex gap-2">
              <button onClick={() => setStep("select")}
                className="bg-gray-100 text-gray-600 rounded-full px-4 py-2.5 text-sm font-bold">← Back</button>
              <button onClick={copyToClipboard}
                className="bg-gray-100 text-gray-600 rounded-full px-4 py-2.5 text-sm font-bold">📋 Copy</button>
              <button onClick={sendMessage} disabled={sending}
                className="flex-1 bg-mouna-blue text-white rounded-full py-2.5 text-sm font-bold disabled:opacity-50">
                {sending ? "Sending..." : "💬 Send Message"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
